import classNames from 'classnames';
import React, { useLayoutEffect, useRef, useEffect } from 'react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside.js';
import styles from './dialog.module.css.js';

function DialogHeader({ children, title, 'data-testid': dataTestId, startAdornment, endAdornment, className, }) {
    const adornment = startAdornment || endAdornment;
    return (React.createElement("header", { "data-testid": dataTestId, className: classNames(styles.header, className, {
            [`bc-dialog__header-has-adornment ${styles.adornment}`]: adornment,
        }), title: title },
        startAdornment && (React.createElement("div", { className: classNames(styles.startAdornment, 'bc-dialog__header-start-adornment') }, startAdornment)),
        React.createElement("div", { className: styles.container },
            React.createElement("p", { className: styles.headline }, title),
            children),
        endAdornment && (React.createElement("div", { className: classNames(styles.endAdornment, 'bc-dialog__header-end-adornment') }, endAdornment))));
}
function DialogBody({ children, 'data-testid': dataTestId, className }) {
    return (React.createElement("div", { "data-testid": dataTestId, className: classNames(styles.body, className) }, children));
}
function DialogFooter({ children, 'data-testid': dataTestId, className }) {
    return (React.createElement("footer", { "data-testid": dataTestId, className: classNames(styles.footer, className) }, children));
}
function ModalDialog({ openModalClassNameForBody = 'bc-modal-dialog--open', zIndex = 1000, ...dialogProps }) {
    /*
     useLayoutEffect is used for react version >= 17.0.0
    */
    useLayoutEffect(() => {
        document.body.classList.add(openModalClassNameForBody);
        return () => {
            const amountOfModalDialogs = document.getElementsByClassName(styles.backdrop);
            if (amountOfModalDialogs.length === 1)
                document.body.classList.remove(openModalClassNameForBody);
        };
    }, [openModalClassNameForBody]);
    return (React.createElement("div", { className: styles.backdrop, style: { zIndex } },
        React.createElement(Dialog, { ...dialogProps })));
}
function Dialog({ id, children, onClose, closeTitle, closeViaEscape = false, closeOnClickOutside = false, fullScreen = false, width = undefined, height = undefined, className = undefined, showCloseButton = true, size = undefined, }) {
    const dialogRef = useRef(null);
    useEffect(() => {
        const keyPressedHandler = (evt) => {
            const { key } = evt;
            if (key === 'Escape')
                onClose();
        };
        if (closeViaEscape)
            window.addEventListener('keydown', keyPressedHandler);
        return () => {
            if (closeViaEscape)
                window.removeEventListener('keydown', keyPressedHandler);
        };
    }, [closeViaEscape, onClose]);
    useOnClickOutside(dialogRef, onClose, !closeOnClickOutside);
    return (React.createElement("div", { id: id, role: 'dialog', className: classNames(styles.dialog, className, {
            [styles.fullScreen]: fullScreen,
            [styles.small]: size === 'small',
        }), ref: dialogRef, style: fullScreen ? {} : { width, height } },
        showCloseButton && (React.createElement("button", { type: 'button', className: styles.close, "data-dismiss": id, "aria-label": closeTitle, title: closeTitle, onClick: () => onClose(), "data-testid": 'dialog-close' },
            React.createElement("span", { "aria-hidden": 'true', className: 'bc-icon bc-icon-close' }))),
        children));
}
Dialog.Header = DialogHeader;
Dialog.Body = DialogBody;
Dialog.Footer = DialogFooter;

export { Dialog, ModalDialog };
